import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusCircle, ClipboardList, UserCheck, RotateCcw, ShieldBan, Trash2, User } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Badge, Button } from '@/components/ui';
import { LeadDetailView } from './LeadDetailView';
import { LeadMergeDialog } from './LeadMergeDialog';
import * as leadsSvc from '@/services/leads.service';
import type { LeadStatus } from '@/services/leads.service';
import { toast } from '@/store/toastStore';

function getStatusBadgeVariant(status: LeadStatus) {
  switch (status) {
    case 'new': return 'info' as const;
    case 'contacted': return 'warning' as const;
    case 'qualified': return 'success' as const;
    case 'converted': return 'success' as const;
    case 'spam': return 'error' as const;
    case 'trash': return 'error' as const;
    default: return 'error' as const;
  }
}

function formatStatus(status: LeadStatus): string {
  switch (status) {
    case 'new': return 'New';
    case 'contacted': return 'Contacted';
    case 'qualified': return 'Qualified';
    case 'converted': return 'Converted';
    case 'spam': return 'Spam';
    case 'trash': return 'Trash';
    case 'dead': return 'Dead';
    default: return status;
  }
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface SelectedPatient {
  patientId: number;
  displayId: string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  cellPhone: string | null;
  birthDate: string | null;
}

export interface LeadDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: number | null;
}

export function LeadDetailModal({
  isOpen,
  onClose,
  leadId,
}: LeadDetailModalProps) {
  if (!isOpen || leadId == null) {
    return null;
  }

  // Keyed on the lead, so a different lead starts with nothing selected.
  return (
    <LeadDetailFrame key={leadId} leadId={leadId} mode="modal" onClose={onClose}>
      {({ header, body }) => (
        <Modal isOpen onClose={onClose} title={header ?? 'Lead Details'} size="xxl">
          {body}
        </Modal>
      )}
    </LeadDetailFrame>
  );
}

export interface LeadDetailFrameProps {
  leadId: number;
  /** Where "not found" sends you: the modal closes, the page goes back to Leads. */
  mode: 'modal' | 'page';
  onClose: () => void;
  /** Lays the parts out: in a Modal (LeadDetailModal) or a page (LeadDetailPage). */
  children: (parts: { header: ReactNode | null; body: ReactNode }) => ReactNode;
}

/**
 * One lead, as the lead modal shows it: the header (status, name, meta and the
 * action buttons) and the body (LeadDetailView), plus the merge step a patient
 * picked in the body opens. The modal and the lead page both render this, so
 * the two look and behave the same. `header` is null until the lead loads.
 */
export function LeadDetailFrame({ leadId, mode, onClose, children }: LeadDetailFrameProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: lead } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => leadsSvc.getLead(leadId),
  });

  const [selectedPatient, setSelectedPatient] = useState<SelectedPatient | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);

  const handlePatientSelect = useCallback((patient: SelectedPatient | null) => {
    setSelectedPatient(patient);
  }, []);

  const restoreMutation = useMutation({
    mutationFn: () => leadsSvc.updateLead(leadId, { status: 'new' as LeadStatus, badReason: '' }),
    onSuccess: () => {
      toast.success('Lead restored');
      void queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: () => toast.error('Failed to restore lead'),
  });

  const markSpamMutation = useMutation({
    mutationFn: () => leadsSvc.markBad(leadId, 'Spam'),
    onSuccess: () => {
      toast.success('Lead marked as spam');
      void queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: () => toast.error('Failed to mark as spam'),
  });

  const markTrashMutation = useMutation({
    mutationFn: () => leadsSvc.markBad(leadId, 'Trash'),
    onSuccess: () => {
      toast.success('Lead moved to trash');
      void queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: () => toast.error('Failed to move to trash'),
  });

  const isConverted = lead?.status === 'converted';
  const isSpam = lead?.status === 'spam';
  const isTrash = lead?.status === 'trash';
  const isActive = !isConverted && !isSpam && !isTrash;

  const header = lead ? (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Badge
            variant={getStatusBadgeVariant(lead.status)}
            dot
            size="sm"
            className={(lead.status === 'spam' || lead.status === 'trash') ? 'bg-red-100 text-red-800 ring-red-300' : undefined}
          >
            {formatStatus(lead.status)}
          </Badge>
          <span className="text-lg font-semibold text-slate-900">{lead.fullName}</span>
          <span className="text-sm text-slate-500">#{lead.leadId}</span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-4 text-sm text-slate-500">
          {lead.dateAdded && <span>Added: <span className="text-slate-700">{formatRelativeDate(lead.dateAdded)}</span></span>}
          {lead.planId && lead.lastContact && (
            <>
              <span>/</span>
              <span>Last Contact: <span className="text-slate-700">{formatRelativeDate(lead.lastContact)}</span></span>
            </>
          )}
        </div>
        <div className="mt-0.5 text-sm text-slate-500">
          Assigned To: <span className="text-slate-700">{lead.assignedToName ?? 'None'}</span>
        </div>
        {lead.mergedPatientId && lead.mergedPatientName && (
          <div className="mt-0.5 text-sm text-slate-500">
            Patient: <button type="button" className="font-semibold text-primary hover:underline" onClick={() => navigate(`/patients/${lead.mergedPatientId}`)}>{lead.mergedPatientName}</button> <span className="text-slate-400">#{lead.mergedPatientId}</span>
          </div>
        )}
        {(lead.planTypeName || lead.planId) && (
          <div className="mt-0.5 flex items-center gap-2 text-sm text-slate-500">
            {lead.planTypeName && lead.planId ? (
              <span>Plan Type: <button type="button" className="text-primary hover:underline" onClick={() => navigate(`/plans/${lead.planId}`)}>{lead.planTypeName}</button></span>
            ) : lead.planTypeName ? (
              <span>Plan Type: <span className="text-slate-700">{lead.planTypeName}</span></span>
            ) : null}
            {lead.stageName && (
              <span>/ Stage: <span className="text-slate-700">{lead.stageName}</span></span>
            )}
          </div>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2">
        {/* Action buttons */}
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            {(isSpam || isTrash) && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<RotateCcw className="h-4 w-4" />}
                onClick={() => restoreMutation.mutate()}
                loading={restoreMutation.isPending}
              >
                {isSpam ? 'Not Spam' : 'Restore from Trash'}
              </Button>
            )}
            {isConverted ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<PlusCircle className="h-4 w-4" />}
                  onClick={() => navigate(`/leads/new?leadId=${leadId}`)}
                >
                  Add Patient
                </Button>
                {lead.mergedPatientId && (
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<User className="h-4 w-4" />}
                    onClick={() => navigate(`/patients/${lead.mergedPatientId}`)}
                  >
                    View Patient
                  </Button>
                )}
                {lead.planId && (
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<ClipboardList className="h-4 w-4" />}
                    onClick={() => navigate(`/plans/${lead.planId}`)}
                  >
                    View Plan
                  </Button>
                )}
              </>
            ) : isActive && !selectedPatient && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<ShieldBan className="h-4 w-4" />}
                  onClick={() => markSpamMutation.mutate()}
                  loading={markSpamMutation.isPending}
                >
                  Spam
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Trash2 className="h-4 w-4" />}
                  onClick={() => markTrashMutation.mutate()}
                  loading={markTrashMutation.isPending}
                >
                  Trash
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<UserCheck className="h-4 w-4" />}
                  onClick={() => navigate(`/leads/new?leadId=${leadId}`)}
                >
                  Convert to Patient
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {children({
        header,
        body: (
          <LeadDetailView
            leadId={leadId}
            mode={mode}
            onClose={onClose}
            onPatientSelect={handlePatientSelect}
            selectedPatientId={selectedPatient?.patientId ?? null}
            onMergeCommLog={() => setMergeOpen(true)}
          />
        ),
      })}

      {selectedPatient && (
        <LeadMergeDialog
          isOpen={mergeOpen}
          onClose={() => setMergeOpen(false)}
          leadId={leadId}
          leadKind="email"
          subjectPreview={lead?.subject ?? lead?.fullName}
          initialPatient={selectedPatient}
          onMerged={() => {
            setMergeOpen(false);
            setSelectedPatient(null);
            void queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
            void queryClient.invalidateQueries({ queryKey: ['leads'] });
          }}
        />
      )}
    </>
  );
}
